import { List, useAccordionItemContext } from '@chakra-ui/react';
import { IQuoteRespSchema, QuoteStatusEnum } from '@monetize/types/app';
import { FC } from 'react';
import { MdInfo } from 'react-icons/md';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
  MBox,
  MFlex,
  MIcon,
  MPageLoader,
  MText,
  MTooltip,
} from '../../../../components/Monetize';
import { useAccordionControl } from '../../../../hooks';
import { useFlags } from '../../../../services/launchDarkly';
import { QuoteDocumentsView } from '../../Quote/components/quoteDocuments/QuoteDocumentsView';
import { useQuoteContext } from '../../Quote/quoteContext';
import { AttachedQuoteDocuments } from './AttachedQuoteDocuments';

interface QuoteUploadDocumentsBodyProps {
  quote: IQuoteRespSchema;
  isReadOnlyQuote: boolean;
  enableQuoteDocumentManagement: boolean;
  handleUpdateQuoteWithFormReset: ReturnType<
    typeof useQuoteContext
  >['handleUpdateQuoteWithFormReset'];
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const QuoteUploadDocumentsBody = ({
  quote,
  isReadOnlyQuote,
  enableQuoteDocumentManagement,
  handleUpdateQuoteWithFormReset,
}: QuoteUploadDocumentsBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();
  const isReadOnly = isReadOnlyQuote || quote?.status !== QuoteStatusEnum.DRAFT;

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        customLabel={
          <>
            <MText ml="2" fontSize="lg" fontWeight="bold">
              Documents
            </MText>
            <MTooltip
              usePopover={true}
              placement="right-start"
              label={
                <MBox fontSize="xs">
                  The Documents in this section will appear on the PDF in the
                  following order:
                  <List.Root as="ul" pl="2">
                    <List.Item>Cover</List.Item>
                    <List.Item>Order Form</List.Item>
                    <List.Item>Footer</List.Item>
                    <List.Item>Statement of Work (SOW)</List.Item>
                    <List.Item>Master Service Agreement (MSA)</List.Item>
                  </List.Root>
                </MBox>
              }
            >
              <MFlex alignItems="center">
                <MIcon as={MdInfo} ml="2" />
              </MFlex>
            </MTooltip>
          </>
        }
        label="Documents"
      ></MAccordionCustomButton>
      <MAccordionPanel p="4" m="0">
        <QuoteDocumentsView
          isReadOnly={isReadOnly}
          quote={quote}
          onUpdate={handleUpdateQuoteWithFormReset}
        />
        {enableQuoteDocumentManagement && (
          <AttachedQuoteDocuments quoteId={quote.id} isReadOnly={isReadOnly} />
        )}
      </MAccordionPanel>
    </>
  );
};

export const QuoteUploadDocuments: FC = () => {
  const { isReadOnlyQuote, quoteData, handleUpdateQuoteWithFormReset } =
    useQuoteContext();

  const { quote } = quoteData;

  const { enableQuoteDocumentManagement } = useFlags();

  const { accordionValue, handleAccordionChange } = useAccordionControl({
    defaultIsExpanded: true,
  });

  if (!quote) {
    return <MPageLoader />;
  }

  return (
    <MAccordion
      multiple
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MAccordionItem value="0" borderColor="tGray.lightGray">
        <QuoteUploadDocumentsBody
          quote={quote}
          isReadOnlyQuote={isReadOnlyQuote}
          enableQuoteDocumentManagement={enableQuoteDocumentManagement}
          handleUpdateQuoteWithFormReset={handleUpdateQuoteWithFormReset}
        />
      </MAccordionItem>
    </MAccordion>
  );
};
